import React from 'react';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { auth } from '../firebase';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  TextField,
} from '@mui/material';

const PasswordResetModal = ({ oobCode, onSuccess, onClose, onRequestNewLink }) => {
  const [phase, setPhase] = React.useState('verifying');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [passwordConfirm, setPasswordConfirm] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  React.useEffect(() => {
    let cancelled = false;
    if (!oobCode) {
      setErrorMessage('This password reset link is no longer valid.');
      setPhase('invalid');
      return;
    }

    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        if (cancelled) return;
        setEmail(verifiedEmail);
        setPhase('ready');
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('verifyPasswordResetCode error:', err);
        const code = err && err.code;
        let message;
        if (code === 'auth/expired-action-code') {
          message = 'This password reset link has expired.';
        } else if (code === 'auth/invalid-action-code' || code === 'auth/user-not-found') {
          message = 'This password reset link is no longer valid.';
        } else if (code === 'auth/user-disabled') {
          message = 'This account has been disabled. Please contact support.';
        } else {
          message =
            "We couldn't validate this link. Please try again or request a new one.";
        }
        setErrorMessage(message);
        setPhase('invalid');
      });

    return () => {
      cancelled = true;
    };
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters.');
      return;
    }
    if (password !== passwordConfirm) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setErrorMessage('');
    setPhase('submitting');
    try {
      await confirmPasswordReset(auth, oobCode, password);
      setPhase('success');
    } catch (err) {
      console.error('confirmPasswordReset error:', err);
      const code = err && err.code;
      if (code === 'auth/weak-password') {
        setErrorMessage('Password must be at least 6 characters.');
        setPhase('ready');
      } else if (
        code === 'auth/expired-action-code' ||
        code === 'auth/invalid-action-code' ||
        code === 'auth/user-disabled' ||
        code === 'auth/user-not-found'
      ) {
        setErrorMessage('This password reset link is no longer valid.');
        setPhase('invalid');
      } else {
        setErrorMessage('Network error. Please try again.');
        setPhase('ready');
      }
    }
  };

  const subtitle =
    phase === 'verifying'
      ? 'Verifying link…'
      : phase === 'success'
      ? 'Password updated'
      : phase === 'invalid'
      ? 'Link no longer valid'
      : 'Set a new password';

  const submitDisabled =
    phase === 'submitting' || password.length < 6 || password !== passwordConfirm;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        sx={{
          width: 420,
          maxWidth: '90vw',
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 4,
          }}
        >
          <Typography
            id="password-reset-dialog-title"
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 1,
              color: '#333333',
            }}
          >
            Reseller Pro
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: '#666666',
              mb: 3,
              fontWeight: 500,
            }}
          >
            {subtitle}
          </Typography>

          {phase === 'verifying' && (
            <Stack spacing={2} sx={{ width: '100%', alignItems: 'center', py: 2 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ color: '#555' }}>
                Verifying link…
              </Typography>
            </Stack>
          )}

          {(phase === 'ready' || phase === 'submitting') && (
            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <Stack spacing={2}>
                {errorMessage && (
                  <Alert severity="error">{errorMessage}</Alert>
                )}
                {email && (
                  <Typography variant="body2" sx={{ color: '#555' }}>
                    Resetting password for <strong>{email}</strong>
                  </Typography>
                )}
                <TextField
                  fullWidth
                  type="password"
                  label="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={phase === 'submitting'}
                  required
                  variant="outlined"
                  autoFocus
                  helperText="Must be at least 6 characters"
                />
                <TextField
                  fullWidth
                  type="password"
                  label="Confirm new password"
                  value={passwordConfirm}
                  onChange={(e) => setPasswordConfirm(e.target.value)}
                  disabled={phase === 'submitting'}
                  required
                  variant="outlined"
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={submitDisabled}
                  startIcon={
                    phase === 'submitting' ? <CircularProgress size={20} /> : null
                  }
                  sx={{
                    height: 48,
                    fontSize: 16,
                    fontWeight: 700,
                    textTransform: 'none',
                  }}
                >
                  {phase === 'submitting' ? 'Updating…' : 'Update password'}
                </Button>
              </Stack>
            </Box>
          )}

          {phase === 'success' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Alert severity="success">Your password has been updated.</Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onSuccess && onSuccess(email)}
                sx={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Sign in
              </Button>
            </Stack>
          )}

          {phase === 'invalid' && (
            <Stack spacing={2} sx={{ width: '100%' }}>
              <Alert severity="error">{errorMessage}</Alert>
              <Button
                variant="contained"
                color="primary"
                onClick={() => onRequestNewLink && onRequestNewLink()}
                sx={{
                  height: 48,
                  fontSize: 16,
                  fontWeight: 700,
                  textTransform: 'none',
                }}
              >
                Request a new reset email
              </Button>
              <Button
                variant="text"
                onClick={() => onClose && onClose()}
                sx={{ textTransform: 'none' }}
              >
                Close
              </Button>
            </Stack>
          )}

        </CardContent>
      </Card>
    </Box>
  );
};

export default PasswordResetModal;
